import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../images/logo.png';
import '../css/DashboardSidebarCollapse.css';
import '../css/UserDashboardPrototype.css';
import '../css/EmsDashboard.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GlobalCompetitionBanner from './GlobalCompetitionBanner';

import {
  faChalkboard, faUser, faUserPlus,
  // faTicket, // DISABLED: Tickets feature not yet implemented
  faHotel, /* faPlaneDeparture, // DISABLED: Travel feature not yet implemented */ faMessage, faDownload,
  // faCircleInfo, // DISABLED: Reports feature not yet implemented
  // faDisplay, // DISABLED: Screens feature not yet implemented
  faGear, /* faFileLines, // DISABLED: Value Lists feature not yet implemented */ faUserGroup,
  // faSearch, // DISABLED: Search feature not yet implemented
  faFolder,
  faTrophy,
  faImage,
  faBuilding,
  faPlus,
  faAnglesLeft,
  faCalendarCheck,
  faEnvelope,
  faClipboardCheck,
  faIdCard,
  faGraduationCap,
  // faBroom, // DISABLED: Data Cleanup feature hidden temporarily
  faBars,
  faTimes,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';

const SIDEBAR_COLLAPSED_KEY = 'dashboardSidebarCollapsed';

const SlashToggle = () => {
  const navigate = useNavigate();
  const isEms = window.location.pathname.startsWith('/ems');
  return (
    <div className="admin-slash-toggle" style={{ fontSize: '18px', cursor: 'pointer' }}>
      <span
        onClick={() => navigate('/crm')}
        style={{
          fontWeight: isEms ? 'normal' : 'bold',
          color: isEms ? '#888' : '#000',
          marginRight: 5,
        }}
      >
        CRM
      </span>
      |
      <span
        onClick={() => navigate('/ems/home-page')}
        style={{
          fontWeight: isEms ? 'bold' : 'normal',
          color: isEms ? '#000' : '#888',
          marginLeft: 5,
        }}
      >
        EMS
      </span>
    </div>
  );
};

const EmsDashboardLayout = ({ handleLogout }) => {
  const username = localStorage.getItem('username');
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navRef = useRef(null);
  const scrollPos = useRef(0);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (nav) nav.scrollTop = scrollPos.current;
    return () => {
      if (nav) scrollPos.current = nav.scrollTop;
    };
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch (_) {}
      return next;
    });
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    if (location.pathname === '/ems' || location.pathname === '/ems/') {
      navigate('/ems/participants', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) setMobileNavOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const navItems = [
    { path: '/ems/home-page', label: 'Dashboard', icon: faChalkboard },
    { path: '/ems/competitions', label: 'Competitions', icon: faTrophy },
    { path: '/ems/participants', label: 'Participants', icon: faUser },
    { path: '/ems/participants/athletes', label: 'Athletes', icon: faUser },
    { path: '/ems/participants/caddies', label: 'Caddies', icon: faUser },
    { path: '/ems/search', label: 'Add Participant', icon: faUserPlus },
    { path: '/ems/participating-organisations', label: 'Participating Orgs', icon: faBuilding },
    { path: '/ems/organisations/search', label: 'Add Organisation', icon: faPlus },
    { path: '/ems/forms', label: 'Forms', icon: faFolder },
    // DISABLED: { path: '#', label: 'Tickets', icon: faTicket },
    { path: '/ems/hotels', label: 'Hotels', icon: faHotel },
    { path: '/ems/bookings', label: 'Bookings', icon: faCalendarCheck },
    // DISABLED: { path: '#', label: 'Travel', icon: faPlaneDeparture },
    { path: '/ems/templates', label: 'Communication', icon: faMessage },
    // DISABLED: { path: '#', label: 'Search', icon: faSearch },
    { path: '/ems/documents-downloads', label: 'Documents/Downloads', icon: faDownload },
    // DISABLED: { path: '#', label: 'Reports', icon: faCircleInfo },
    // DISABLED: { path: '#', label: 'Screens', icon: faDisplay },
    { path: '/ems/changelog', label: 'Change Tracking', icon: faGear },
    // DISABLED: { path: '#', label: 'Value Lists', icon: faFileLines },
    { path: '/ems/groups', label: 'Groups', icon: faUserGroup },
    { path: '/ems/roles', label: 'Roles', icon: faGraduationCap },
    { path: '/ems/invitation-types', label: 'Invitation Types', icon: faEnvelope },
    { path: '/ems/accreditation-categories', label: 'Accreditation Categories', icon: faIdCard },
    { path: '/ems/image-approval', label: 'Image Approval', icon: faImage },
    { path: '/ems/onsite-registration', label: 'Onsite Registration', icon: faClipboardCheck },
    // DISABLED: { path: '/ems/data-cleanup', label: 'Data Cleanup', icon: faBroom },
  ];

  const isActive = (path) => {
    if (path === '/ems/participants') {
      return (
        location.pathname === '/ems' ||
        location.pathname === '/ems/' ||
        location.pathname === '/ems/participants'
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div
      className={[
        'd-flex user-dashboard-shell',
        sidebarCollapsed ? 'user-dashboard-shell--collapsed' : '',
        mobileNavOpen ? 'user-dashboard-shell--nav-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="user-sidebar-backdrop"
        aria-label="Close menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={closeMobileNav}
      />

      <div
        className={`user-sidebar-container d-flex flex-column ${sidebarCollapsed ? 'collapsed' : ''}`}
        id="ems-sidebar"
      >
        <div className="user-logo-container d-flex align-items-center justify-content-center">
          <img src={logo} alt="EMS Logo" className="user-logo" />
          <span className="user-logo-monogram" aria-hidden="true">IGF</span>
          <button
            type="button"
            className="user-sidebar-close"
            aria-label="Close menu"
            onClick={closeMobileNav}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="sidebar flex-grow-1">
          <ul className="nav flex-column px-0" ref={navRef}>
            {navItems.map((item) => (
              <li className="nav-item" key={item.path}>
                <Link
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  title={item.label}
                  onClick={closeMobileNav}
                >
                  <span className="nav-icon-circle">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="sidebar-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="sidebar-footer">
            <button
              type="button"
              className="sidebar-collapse-row"
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <span className="sidebar-collapse-icon">
                <FontAwesomeIcon icon={faAnglesLeft} />
              </span>
              <span className="sidebar-footer-label">
                {sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="content d-flex flex-column flex-grow-1">
        <nav className="navbar navbar-light user-topbar">
          <button
            type="button"
            className="user-topbar__menu-btn"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
            aria-controls="ems-sidebar"
            onClick={() => setMobileNavOpen(true)}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className="user-topbar__leading">
            <SlashToggle />
          </div>

          <div className="user-topbar__banner">
            <GlobalCompetitionBanner />
          </div>

          <h4 className="navbar-brand mb-0 user-topbar__welcome">
            <div className="dropdown">
              <button
                className="btn btn-light dropdown-toggle"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Welcome, {username || 'Guest'}
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li>
                  <Link className="dropdown-item" to="/profile">
                    Profile
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/change-password">
                    Change Password
                  </Link>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </h4>
        </nav>

        <div className="d-flex flex-grow-1 user-dashboard-body">
          <div className="content-full-width">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmsDashboardLayout;
